import { CommentInput } from 'components/Comments'
import { IComment, IHandleCreateComment, RootState } from 'constants/interfaces'
import React, { memo, useCallback, useMemo } from 'react'
import { MessagePreview } from '../MessagePreview'
import { Box } from '@chakra-ui/core'
import { extractRefComment } from '../utils'
import { Post } from 'redux/reducers/feed'
import keys from 'constants/keys'
import { AppDispatch } from 'config/redux'
import { useDispatch } from 'react-redux'
import { setCommentsCounter, updateComment } from 'redux/actions/comments'
import { setCommentContext } from 'redux/actions/feed'

interface IProps {
  chat: Post
  message: IComment
  replies: IComment[]
  commentContext: RootState['feed']['commentContext']
  sendReply: (params: {
    id: string
    comment: string
    rasID: string
    objectType: number
    parentCID: string
    replyCID: string
    createdByUserId: string | undefined
    images?: any[]
    videos?: any[]
    audios?: any
    files?: any[]
    isChat: boolean
    ref_comment?: any
  }) => Promise<string | undefined>
  sendReplyLoading: boolean
  repliesCounter: number
  uid: string | undefined
}

export const AnnouncementCommentModalFooter = memo(
  ({ chat, message, replies, commentContext, sendReply, sendReplyLoading, repliesCounter, uid }: IProps) => {
    const dispatch: AppDispatch = useDispatch()

    const commentContextCommentId = commentContext.commentId
    const commentContextIsEdit = commentContext.isEdit

    const replyingComment = useMemo(
      () => replies.find((reply) => reply.cid === commentContextCommentId),
      [replies, commentContextCommentId]
    )

    const replyingCommentMessage = replyingComment?.message

    const refComment = useMemo(() => extractRefComment(replyingComment), [replyingComment])

    const initialCommentText = useMemo(
      () => (commentContextIsEdit ? replyingCommentMessage : '') || '',
      [commentContextIsEdit, replyingCommentMessage]
    )

    const handleCloseReply = useCallback(() => {
      dispatch(setCommentContext({ commentId: null, isEdit: false }))
    }, [dispatch])

    const handleCreateReply = useCallback(
      async ({ comment, images, videos, audios, files }: IHandleCreateComment) => {
        if (!chat || !message) return
        handleCloseReply()

        const replyType = keys.COMMENT_OBJECT_TYPE.COMMENT_ON_ANNOUNCEMENT_GROUP_MESSAGE
        const objectType = refComment ? keys.COMMENT_OBJECT_TYPE.REF_REPLY_ON_REPLY : replyType

        try {
          const commentID = await sendReply({
            id: chat.post_id,
            comment,
            rasID: chat.ras_id || '',
            objectType: objectType,
            parentCID: message.cid,
            replyCID: message.cid,
            createdByUserId: uid,
            images,
            videos,
            audios,
            files,
            isChat: true,
            ref_comment: refComment,
          })

          if (commentID) {
            dispatch(setCommentsCounter({ count: repliesCounter + 1, key: message.cid, isReply: true }))
          }
        } catch (error) {
          console.log(error)
        }
      },
      [chat, message, handleCloseReply, refComment, sendReply, uid, dispatch, repliesCounter]
    )

    const handleEditMessage = useCallback(
      async ({ comment }: IHandleCreateComment) => {
        if (!chat || !message) return
        handleCloseReply()
        dispatch(
          updateComment({
            oid: chat.post_id,
            cid: commentContextCommentId!,
            reaction: '',
            object_created_by: chat.created_by,
            groups: chat.groups,
            tags: chat.tags,
            users: chat.users,
            message: comment,
            reply_cid: message.cid,
          })
        )
      },
      [chat, message, handleCloseReply, dispatch, commentContextCommentId]
    )

    const targetAction = useMemo(
      () => (commentContextIsEdit ? handleEditMessage : handleCreateReply),
      [commentContextIsEdit, handleEditMessage, handleCreateReply]
    )

    return (
      <Box p="10px" w="100%">
        {replyingComment && <MessagePreview comment={replyingComment} onClose={handleCloseReply} />}
        <CommentInput
          isFocused={false}
          postID={chat.post_id}
          handleCreateComment={targetAction}
          sendCommentLoading={sendReplyLoading}
          initialCommentText={initialCommentText}
        />
      </Box>
    )
  }
)
